import { LanguageCode } from "../../utils/translations";

export type CapabilityCategory = "Social Media" | "Automation";

export interface HomeCapability {
  /** Drives the tag colour, not the displayed label. */
  category: CapabilityCategory;
  /** Displayed label (localised). */
  tag: string;
  title: string;
  desc: string;
  to: string;
}

export interface HomeStage {
  key: string;
  step: string;
  title: string;
  desc: string;
}

export interface HomePlaybook {
  title: string;
  steps: string[];
  to: string;
}

export interface HomeServiceSlide {
  title: string;
  tagline: string;
  tag: string;
}

export interface HomeCopy {
  hero: {
    badge: string;
    headingPre: string;
    headingHighlight: string;
    intro: string;
    alsoLabel: string;
    alsoList: string;
    primaryBtn: string;
    secondaryBtn: string;
    badges: string[];
    showcaseLabel: string;
    live: string;
    explore: string;
  };
  capabilities: {
    eyebrow: string;
    title: string;
    subtitle: string;
    items: HomeCapability[];
  };
  howItWorks: {
    eyebrow: string;
    title: string;
    subtitle: string;
    stages: HomeStage[];
  };
  playbooksSection: {
    eyebrow: string;
    title: string;
    subtitle: string;
    linkLabel: string;
    items: HomePlaybook[];
  };
  band: {
    lead: string;
    chips: { label: string; to: string }[];
    seeAll: string;
  };
  reviews: {
    title: string;
    items: { name: string; text: string }[];
  };
  finalCta: {
    badge: string;
    title: string;
    subtitle: string;
    button: string;
  };
  serviceSlides: Record<string, { title: string; tagline: string; tag: string }>;
}

const EN: HomeCopy = {
  hero: {
    badge: "AUTOMATION-FIRST TECHNOLOGY PARTNER",
    headingPre: "Put your business on ",
    headingHighlight: "autopilot.",
    intro:
      "SocialFlow brings together the tools you already use — Instagram, Facebook, WhatsApp, email, SMS, your customer lists and more — so everything sits in one place. Then it does the daily work for you: replying to messages, following up with leads and keeping every contact up to date. You grow your business; it handles the rest.",
    alsoLabel: "Also on the platform:",
    alsoList:
      "AI development & AI agents · CRM & contacts · APIs and system integrations · cloud, servers & IT resources · SMS & email services · website and mobile app development · multi-account management.",
    primaryBtn: "Start Automating",
    secondaryBtn: "Explore Solutions",
    badges: [
      "Connects to your existing tools",
      "Handles follow-ups automatically",
      "Runs on your schedule",
      "Keeps leads from falling through",
      "Gives one simple dashboard",
    ],
    showcaseLabel: "automation.platform",
    live: "LIVE",
    explore: "Explore",
  },
  capabilities: {
    eyebrow: "What you can do",
    title: "Social media and automation, side by side",
    subtitle:
      "Scroll to see how SocialFlow grows your reach and automates the work behind it.",
    items: [
      {
        category: "Social Media",
        tag: "Social Media",
        title: "Post & Schedule",
        desc: "Plan and publish across every platform — no daily logins.",
        to: "/social",
      },
      {
        category: "Social Media",
        tag: "Social Media",
        title: "Multi-Account Manager",
        desc: "Run many Instagram, Facebook and other accounts together.",
        to: "/multi-account-manager",
      },
      {
        category: "Social Media",
        tag: "Social Media",
        title: "Comments & DMs",
        desc: "Auto-reply and route enquiries so you never miss one.",
        to: "/social-media-automation",
      },
      {
        category: "Social Media",
        tag: "Social Media",
        title: "YouTube Tag Finder",
        desc: "Find high-ranking tags so your videos get found.",
        to: "/social",
      },
      {
        category: "Automation",
        tag: "Automation",
        title: "Lead Capture",
        desc: "Leads from forms and ads flow straight into your pipeline.",
        to: "/automate-business",
      },
      {
        category: "Automation",
        tag: "Automation",
        title: "Follow-ups",
        desc: "Automatic Email, WhatsApp and SMS messages after interest.",
        to: "/automate-business",
      },
      {
        category: "Automation",
        tag: "Automation",
        title: "CRM & Contacts",
        desc: "Keep every lead and customer updated in one place.",
        to: "/multi-account-manager",
      },
      {
        category: "Automation",
        tag: "Automation",
        title: "Reports",
        desc: "See leads, clicks and posts working in one simple report.",
        to: "/dashboard",
      },
      {
        category: "Automation",
        tag: "Automation",
        title: "AI Content Repurposing",
        desc: "Turn one post into ready-to-use content for each platform.",
        to: "/automate-business",
      },
    ],
  },
  howItWorks: {
    eyebrow: "How it works",
    title: "How your work flows automatically",
    subtitle:
      "When something happens, SocialFlow takes the next step for you — from first enquiry to follow-up.",
    stages: [
      {
        key: "trigger",
        step: "01",
        title: "Something happens",
        desc: "A new lead, comment, enquiry or scheduled time starts it.",
      },
      {
        key: "act",
        step: "02",
        title: "It takes action",
        desc: "Posts, replies, follows up or updates your records automatically.",
      },
      {
        key: "follow",
        step: "03",
        title: "It keeps going",
        desc: "Sends timely follow-ups until your lead responds or is sorted.",
      },
      {
        key: "report",
        step: "04",
        title: "You see results",
        desc: "Everything shows up in one simple dashboard for you to review.",
      },
    ],
  },
  playbooksSection: {
    eyebrow: "Example flows",
    title: "Everyday jobs, done for you",
    subtitle:
      "Real examples of social media joined up with your day-to-day work — each one runs without manual effort.",
    linkLabel: "See how it works",
    items: [
      {
        title: "Turn one post into many",
        steps: [
          "Write once",
          "Auto-create for each platform",
          "Post at the right time",
          "See which ones work best",
        ],
        to: "/automate-business",
      },
      {
        title: "Capture & follow up on leads",
        steps: [
          "Lead comes from your form or ad",
          "Instant message sent",
          "Timed follow-ups if no reply",
          "All leads tracked in one place",
        ],
        to: "/automate-business",
      },
      {
        title: "Never miss a comment or DM",
        steps: [
          "Auto-spot new enquiries",
          "Send a quick reply right away",
          "Pass to you if it needs a human",
          "Keep everything in one view",
        ],
        to: "/social-media-automation",
      },
      {
        title: "Simple reports for your business",
        steps: [
          "All posts & leads in one place",
          "See clicks, replies & enquiries",
          "Spot what's working",
          "Spot what is working",
        ],
        to: "/dashboard",
      },
    ],
  },
  band: {
    lead: "Social media is one piece — we also build the rest of your stack:",
    chips: [
      { label: "AI & ML", to: "/services/ai-development" },
      { label: "AI Agents", to: "/services/ai-agents" },
      { label: "API & Integrations", to: "/services/api-development" },
      { label: "CRM", to: "/services/crm-integration" },
      { label: "Cloud & Servers", to: "/services/cloud-infrastructure" },
      { label: "SMS & Email", to: "/services/messaging-services" },
      { label: "Mobile Apps", to: "/services/mobile-app-development" },
      { label: "Websites", to: "/services/website-development" },
      {
        label: "Workflow Automation",
        to: "/services/mobile-workflow-automation",
      },
    ],
    seeAll: "See all services",
  },
  reviews: {
    title: "What Our Clients Say",
    items: [
      {
        name: "Aqua Tiles",
        text: "SocialFlow helped us automate inquiries and increase showroom visits.",
      },
      {
        name: "English Tutor from Mysore",
        text: "I never knew branding mattered until SocialFlow structured my presence.",
      },
      {
        name: "Pallav",
        text: "Got more land sale inquiries in 30 days than last 6 months.",
      },
    ],
  },
  finalCta: {
    badge: "GET STARTED",
    title: "Ready to put your business on autopilot?",
    subtitle:
      "Tell us where you are stuck and we will map the automation that saves you the most time — free.",
    button: "Book a Free Audit",
  },
  serviceSlides: {
    "ai-development": {
      title: "AI Development",
      tagline: "Put AI to work inside your business.",
      tag: "AI & Machine Learning",
    },
    "ai-agents": {
      title: "AI Agents",
      tagline: "Digital team-mates that finish the task.",
      tag: "AI & Machine Learning",
    },
    "api-development": {
      title: "API Development",
      tagline: "Connect systems so data moves itself.",
      tag: "Software & Integrations",
    },
    "crm-integration": {
      title: "CRM Deployment & Integration",
      tagline: "Every lead and customer in one place.",
      tag: "Software & Integrations",
    },
    "system-integrations": {
      title: "System Integrations",
      tagline: "Make all your tools work as one.",
      tag: "Software & Integrations",
    },
    "cloud-infrastructure": {
      title: "Cloud, Servers & Infrastructure",
      tagline: "Reliable hosting, plus experts on demand.",
      tag: "Cloud & Infrastructure",
    },
    "messaging-services": {
      title: "SMS & Email Services",
      tagline: "Reach customers where they actually look.",
      tag: "Communication",
    },
    "website-development": {
      title: "Website Development",
      tagline: "Fast, modern sites that convert.",
      tag: "Apps & Automation",
    },
    "mobile-app-development": {
      title: "Mobile App Development",
      tagline: "Apps your customers and team love to use.",
      tag: "Apps & Automation",
    },
    "mobile-workflow-automation": {
      title: "Mobile Workflow Automation",
      tagline: "Your processes, running from a phone.",
      tag: "Apps & Automation",
    },
  },
};

const hi: HomeCopy = {
  hero: {
    badge: "ऑटोमेशन-फर्स्ट टेक्नोलॉजी पार्टनर",
    headingPre: "अपने बिज़नेस को ",
    headingHighlight: "ऑटोपायलट पर चलाएं।",
    intro:
      "SocialFlow आपके पहले से इस्तेमाल हो रहे टूल्स — इंस्टाग्राम, फेसबुक, व्हाट्सएप, ईमेल, SMS, आपकी कस्टमर लिस्ट और भी बहुत कुछ — को एक जगह जोड़ देता है। फिर यह आपके रोज़मर्रा के काम खुद करता है: मैसेज का जवाब देना, लीड्स को फॉलो-अप भेजना और हर कॉन्टैक्ट को अपडेट रखना। आप अपना बिज़नेस बढ़ाएं; बाकी सब यह संभाल लेता है।",
    alsoLabel: "प्लेटफ़ॉर्म पर यह भी:",
    alsoList:
      "AI डेवलपमेंट और AI एजेंट्स · CRM और कॉन्टैक्ट्स · API और सिस्टम इंटीग्रेशन · क्लाउड, सर्वर और IT रिसोर्स · SMS और ईमेल सर्विसेज़ · वेबसाइट और मोबाइल ऐप डेवलपमेंट · मल्टी-अकाउंट मैनेजमेंट।",
    primaryBtn: "ऑटोमेशन शुरू करें",
    secondaryBtn: "सॉल्यूशंस देखें",
    badges: [
      "आपके मौजूदा टूल्स से जुड़ता है",
      "फॉलो-अप अपने आप करता है",
      "आपके शेड्यूल पर चलता है",
      "लीड्स छूटने नहीं देता",
      "एक आसान डैशबोर्ड देता है",
    ],
    showcaseLabel: "automation.platform",
    live: "लाइव",
    explore: "देखें",
  },
  capabilities: {
    eyebrow: "आप क्या कर सकते हैं",
    title: "सोशल मीडिया और ऑटोमेशन, साथ-साथ",
    subtitle:
      "देखें कि SocialFlow कैसे आपकी पहुंच बढ़ाता है और पीछे का काम ऑटोमेट करता है।",
    items: [
      {
        category: "Social Media",
        tag: "सोशल मीडिया",
        title: "पोस्ट और शेड्यूल",
        desc: "हर प्लेटफ़ॉर्म पर पोस्ट प्लान और पब्लिश करें — रोज़ लॉगिन करने की जरूरत नहीं।",
        to: "/social",
      },
      {
        category: "Social Media",
        tag: "सोशल मीडिया",
        title: "मल्टी-अकाउंट मैनेजर",
        desc: "कई इंस्टाग्राम, फेसबुक और अन्य अकाउंट्स एक साथ चलाएं।",
        to: "/multi-account-manager",
      },
      {
        category: "Social Media",
        tag: "सोशल मीडिया",
        title: "कमेंट्स और DMs",
        desc: "अपने आप जवाब दें और पूछताछ आगे भेजें ताकि कोई न छूटे।",
        to: "/social-media-automation",
      },
      {
        category: "Social Media",
        tag: "सोशल मीडिया",
        title: "YouTube टैग खोजक",
        desc: "हाई-रैंकिंग टैग खोजें ताकि आपके वीडियो मिलें।",
        to: "/social",
      },
      {
        category: "Automation",
        tag: "ऑटोमेशन",
        title: "लीड कैप्चर",
        desc: "फॉर्म और विज्ञापनों से लीड्स सीधे आपकी पाइपलाइन में आती हैं।",
        to: "/automate-business",
      },
      {
        category: "Automation",
        tag: "ऑटोमेशन",
        title: "फॉलो-अप",
        desc: "दिलचस्पी दिखाने के बाद ईमेल, व्हाट्सएप और SMS मैसेज अपने आप।",
        to: "/automate-business",
      },
      {
        category: "Automation",
        tag: "ऑटोमेशन",
        title: "CRM और कॉन्टैक्ट्स",
        desc: "हर लीड और ग्राहक को एक जगह अपडेट रखें।",
        to: "/multi-account-manager",
      },
      {
        category: "Automation",
        tag: "ऑटोमेशन",
        title: "रिपोर्ट्स",
        desc: "लीड्स, क्लिक्स और पोस्ट्स को एक आसान रिपोर्ट में देखें।",
        to: "/dashboard",
      },
      {
        category: "Automation",
        tag: "ऑटोमेशन",
        title: "AI कंटेंट रीपरपज़िंग",
        desc: "एक पोस्ट को हर प्लेटफ़ॉर्म के लिए तैयार कंटेंट में बदलें।",
        to: "/automate-business",
      },
    ],
  },
  howItWorks: {
    eyebrow: "यह कैसे काम करता है",
    title: "आपका काम अपने आप कैसे चलता है",
    subtitle:
      "जब कुछ होता है, SocialFlow आपके लिए अगला कदम उठाता है — पहली पूछताछ से लेकर फॉलो-अप तक।",
    stages: [
      {
        key: "trigger",
        step: "01",
        title: "कुछ होता है",
        desc: "नई लीड, कमेंट, पूछताछ या तय समय इसकी शुरुआत करता है।",
      },
      {
        key: "act",
        step: "02",
        title: "यह कार्रवाई करता है",
        desc: "पोस्ट करता है, जवाब देता है, फॉलो-अप भेजता है या आपके रिकॉर्ड अपने आप अपडेट करता है।",
      },
      {
        key: "follow",
        step: "03",
        title: "यह चलता रहता है",
        desc: "जब तक लीड जवाब न दे या बात बन न जाए, समय-समय पर फॉलो-अप भेजता रहता है।",
      },
      {
        key: "report",
        step: "04",
        title: "आपको नतीजे दिखते हैं",
        desc: "सब कुछ एक आसान डैशबोर्ड में दिखता है, ताकि आप देख सकें।",
      },
    ],
  },
  playbooksSection: {
    eyebrow: "उदाहरण फ़्लो",
    title: "रोज़ के काम, आपके लिए अपने आप",
    subtitle:
      "सोशल मीडिया को आपके रोज़ के काम से जोड़ने के असली उदाहरण — हर एक बिना मैन्युअल मेहनत चलता है।",
    linkLabel: "देखें यह कैसे काम करता है",
    items: [
      {
        title: "एक पोस्ट से कई पोस्ट बनाएं",
        steps: [
          "एक बार लिखें",
          "हर प्लेटफ़ॉर्म के लिए अपने आप बनाएं",
          "सही समय पर पोस्ट करें",
          "देखें कौन सी सबसे अच्छी चलती हैं",
        ],
        to: "/automate-business",
      },
      {
        title: "लीड्स कैप्चर करें और फॉलो-अप करें",
        steps: [
          "लीड आपके फॉर्म या विज्ञापन से आती है",
          "तुरंत मैसेज भेजा जाता है",
          "जवाब न आने पर समय पर फॉलो-अप",
          "सभी लीड्स एक जगह ट्रैक होती हैं",
        ],
        to: "/automate-business",
      },
      {
        title: "कोई कमेंट या DM न छूटे",
        steps: [
          "नई पूछताछ अपने आप पहचानें",
          "तुरंत छोटा जवाब भेजें",
          "जरूरत पड़े तो आपको भेजें",
          "सब कुछ एक ही जगह देखें",
        ],
        to: "/social-media-automation",
      },
      {
        title: "आपके बिज़नेस के लिए आसान रिपोर्ट्स",
        steps: [
          "सभी पोस्ट और लीड्स एक जगह",
          "क्लिक्स, रिप्लाई और पूछताछ देखें",
          "समझें क्या काम कर रहा है",
          "किस पर ध्यान दें, यह पता करें",
        ],
        to: "/dashboard",
      },
    ],
  },
  band: {
    lead: "सोशल मीडिया एक हिस्सा है — हम आपका बाकी स्टैक भी बनाते हैं:",
    chips: [
      { label: "AI और ML", to: "/services/ai-development" },
      { label: "AI एजेंट्स", to: "/services/ai-agents" },
      { label: "API और इंटीग्रेशन", to: "/services/api-development" },
      { label: "CRM", to: "/services/crm-integration" },
      { label: "क्लाउड और सर्वर", to: "/services/cloud-infrastructure" },
      { label: "SMS और ईमेल", to: "/services/messaging-services" },
      { label: "मोबाइल ऐप्स", to: "/services/mobile-app-development" },
      { label: "वेबसाइट्स", to: "/services/website-development" },
      {
        label: "वर्कफ़्लो ऑटोमेशन",
        to: "/services/mobile-workflow-automation",
      },
    ],
    seeAll: "सभी सर्विसेज़ देखें",
  },
  reviews: {
    title: "हमारे क्लाइंट्स क्या कहते हैं",
    items: [
      {
        name: "Aqua Tiles",
        text: "SocialFlow ने हमारी पूछताछ ऑटोमेट करने और शोरूम विज़िट बढ़ाने में मदद की।",
      },
      {
        name: "मैसूर से इंग्लिश ट्यूटर",
        text: "SocialFlow ने मेरी मौजूदगी को व्यवस्थित किया, तब समझ आया कि ब्रांडिंग कितनी मायने रखती है।",
      },
      {
        name: "पल्लव",
        text: "30 दिनों में पिछले 6 महीनों से ज़्यादा ज़मीन बिक्री की पूछताछ मिलीं।",
      },
    ],
  },
  finalCta: {
    badge: "शुरू करें",
    title: "अपने बिज़नेस को ऑटोपायलट पर चलाने के लिए तैयार हैं?",
    subtitle:
      "बताएं आप कहां अटके हैं और हम वह ऑटोमेशन तैयार करेंगे जो आपका सबसे ज़्यादा समय बचाए — बिल्कुल फ्री।",
    button: "फ्री ऑडिट बुक करें",
  },
  serviceSlides: {
    "ai-development": {
      title: "AI डेवलपमेंट",
      tagline: "AI को अपने बिज़नेस में काम पर लगाएं।",
      tag: "AI और मशीन लर्निंग",
    },
    "ai-agents": {
      title: "AI एजेंट्स",
      tagline: "काम पूरा करने वाले डिजिटल साथी।",
      tag: "AI और मशीन लर्निंग",
    },
    "api-development": {
      title: "API डेवलपमेंट",
      tagline: "सिस्टम्स को जोड़ें ताकि डेटा खुद चले।",
      tag: "सॉफ़्टवेयर और इंटीग्रेशन",
    },
    "crm-integration": {
      title: "CRM सेटअप और इंटीग्रेशन",
      tagline: "हर लीड और ग्राहक एक जगह।",
      tag: "सॉफ़्टवेयर और इंटीग्रेशन",
    },
    "system-integrations": {
      title: "सिस्टम इंटीग्रेशन",
      tagline: "आपके सभी टूल्स को एक साथ चलाएं।",
      tag: "सॉफ़्टवेयर और इंटीग्रेशन",
    },
    "cloud-infrastructure": {
      title: "क्लाउड, सर्वर और इन्फ्रास्ट्रक्चर",
      tagline: "भरोसेमंद होस्टिंग, और जरूरत पर एक्सपर्ट्स।",
      tag: "क्लाउड और इन्फ्रास्ट्रक्चर",
    },
    "messaging-services": {
      title: "SMS और ईमेल सर्विसेज़",
      tagline: "ग्राहकों तक वहीं पहुंचें जहां वे असल में देखते हैं।",
      tag: "कम्युनिकेशन",
    },
    "website-development": {
      title: "वेबसाइट डेवलपमेंट",
      tagline: "तेज़, आधुनिक वेबसाइट जो ग्राहक लाएं।",
      tag: "ऐप्स और ऑटोमेशन",
    },
    "mobile-app-development": {
      title: "मोबाइल ऐप डेवलपमेंट",
      tagline: "ऐप्स जो आपके ग्राहक और टीम पसंद करें।",
      tag: "ऐप्स और ऑटोमेशन",
    },
    "mobile-workflow-automation": {
      title: "मोबाइल वर्कफ़्लो ऑटोमेशन",
      tagline: "आपकी प्रक्रियाएं, फोन से चलती हुईं।",
      tag: "ऐप्स और ऑटोमेशन",
    },
  },
};

const fr: HomeCopy = {
  hero: {
    badge: "PARTENAIRE TECHNOLOGIQUE AXÉ AUTOMATISATION",
    headingPre: "Mettez votre entreprise en ",
    headingHighlight: "pilote automatique.",
    intro:
      "SocialFlow réunit les outils que vous utilisez déjà — Instagram, Facebook, WhatsApp, e-mail, SMS, vos listes clients et bien plus — pour que tout soit au même endroit. Ensuite, il fait le travail quotidien à votre place : répondre aux messages, relancer les prospects et tenir chaque contact à jour. Vous développez votre entreprise ; il s'occupe du reste.",
    alsoLabel: "Également sur la plateforme :",
    alsoList:
      "Développement IA et agents IA · CRM et contacts · API et intégrations système · cloud, serveurs et ressources IT · services SMS et e-mail · développement de sites web et d'applications mobiles · gestion multi-comptes.",
    primaryBtn: "Commencer l'automatisation",
    secondaryBtn: "Explorer les solutions",
    badges: [
      "Se connecte à vos outils existants",
      "Gère les relances automatiquement",
      "Fonctionne selon votre planning",
      "Empêche les prospects de passer à la trappe",
      "Offre un tableau de bord simple",
    ],
    showcaseLabel: "automation.platform",
    live: "EN DIRECT",
    explore: "Explorer",
  },
  capabilities: {
    eyebrow: "Ce que vous pouvez faire",
    title: "Réseaux sociaux et automatisation, côte à côte",
    subtitle:
      "Faites défiler pour voir comment SocialFlow développe votre portée et automatise le travail derrière.",
    items: [
      {
        category: "Social Media",
        tag: "Réseaux sociaux",
        title: "Publier et planifier",
        desc: "Planifiez et publiez sur toutes les plateformes — sans connexion quotidienne.",
        to: "/social",
      },
      {
        category: "Social Media",
        tag: "Réseaux sociaux",
        title: "Gestionnaire multi-comptes",
        desc: "Gérez plusieurs comptes Instagram, Facebook et autres ensemble.",
        to: "/multi-account-manager",
      },
      {
        category: "Social Media",
        tag: "Réseaux sociaux",
        title: "Commentaires et DM",
        desc: "Répondez automatiquement et orientez les demandes pour n'en manquer aucune.",
        to: "/social-media-automation",
      },
      {
        category: "Social Media",
        tag: "Réseaux sociaux",
        title: "Recherche de tags YouTube",
        desc: "Trouvez des tags bien classés pour que vos vidéos soient trouvées.",
        to: "/social",
      },
      {
        category: "Automation",
        tag: "Automatisation",
        title: "Capture de prospects",
        desc: "Les prospects des formulaires et des publicités arrivent directement dans votre pipeline.",
        to: "/automate-business",
      },
      {
        category: "Automation",
        tag: "Automatisation",
        title: "Relances",
        desc: "Messages e-mail, WhatsApp et SMS automatiques après un intérêt.",
        to: "/automate-business",
      },
      {
        category: "Automation",
        tag: "Automatisation",
        title: "CRM et contacts",
        desc: "Gardez chaque prospect et client à jour au même endroit.",
        to: "/multi-account-manager",
      },
      {
        category: "Automation",
        tag: "Automatisation",
        title: "Rapports",
        desc: "Voyez prospects, clics et publications dans un seul rapport simple.",
        to: "/dashboard",
      },
      {
        category: "Automation",
        tag: "Automatisation",
        title: "Réutilisation de contenu par IA",
        desc: "Transformez une publication en contenu prêt à l'emploi pour chaque plateforme.",
        to: "/automate-business",
      },
    ],
  },
  howItWorks: {
    eyebrow: "Comment ça marche",
    title: "Comment votre travail s'automatise",
    subtitle:
      "Quand quelque chose se produit, SocialFlow passe à l'étape suivante pour vous — de la première demande à la relance.",
    stages: [
      {
        key: "trigger",
        step: "01",
        title: "Il se passe quelque chose",
        desc: "Un nouveau prospect, commentaire, une demande ou une heure planifiée le déclenche.",
      },
      {
        key: "act",
        step: "02",
        title: "Il agit",
        desc: "Publie, répond, relance ou met à jour vos fiches automatiquement.",
      },
      {
        key: "follow",
        step: "03",
        title: "Il continue",
        desc: "Envoie des relances au bon moment jusqu'à ce que votre prospect réponde ou soit réglé.",
      },
      {
        key: "report",
        step: "04",
        title: "Vous voyez les résultats",
        desc: "Tout apparaît dans un tableau de bord simple à consulter.",
      },
    ],
  },
  playbooksSection: {
    eyebrow: "Exemples de flux",
    title: "Les tâches du quotidien, faites pour vous",
    subtitle:
      "Des exemples concrets de réseaux sociaux reliés à votre travail quotidien — chacun fonctionne sans effort manuel.",
    linkLabel: "Voir comment ça marche",
    items: [
      {
        title: "Transformez une publication en plusieurs",
        steps: [
          "Écrivez une fois",
          "Création automatique pour chaque plateforme",
          "Publiez au bon moment",
          "Voyez ce qui marche le mieux",
        ],
        to: "/automate-business",
      },
      {
        title: "Capturer et relancer les prospects",
        steps: [
          "Le prospect vient de votre formulaire ou publicité",
          "Message instantané envoyé",
          "Relances programmées sans réponse",
          "Tous les prospects suivis au même endroit",
        ],
        to: "/automate-business",
      },
      {
        title: "Ne manquez jamais un commentaire ou DM",
        steps: [
          "Détectez automatiquement les nouvelles demandes",
          "Envoyez une réponse rapide tout de suite",
          "Transmettez si un humain est nécessaire",
          "Gardez tout dans une seule vue",
        ],
        to: "/social-media-automation",
      },
      {
        title: "Des rapports simples pour votre entreprise",
        steps: [
          "Toutes les publications et prospects au même endroit",
          "Voyez clics, réponses et demandes",
          "Repérez ce qui fonctionne",
          "Découvrez ce qui marche",
        ],
        to: "/dashboard",
      },
    ],
  },
  band: {
    lead: "Les réseaux sociaux ne sont qu'une pièce — nous construisons aussi le reste de votre stack :",
    chips: [
      { label: "IA et ML", to: "/services/ai-development" },
      { label: "Agents IA", to: "/services/ai-agents" },
      { label: "API et intégrations", to: "/services/api-development" },
      { label: "CRM", to: "/services/crm-integration" },
      { label: "Cloud et serveurs", to: "/services/cloud-infrastructure" },
      { label: "SMS et e-mail", to: "/services/messaging-services" },
      { label: "Applications mobiles", to: "/services/mobile-app-development" },
      { label: "Sites web", to: "/services/website-development" },
      {
        label: "Automatisation des flux de travail",
        to: "/services/mobile-workflow-automation",
      },
    ],
    seeAll: "Voir tous les services",
  },
  reviews: {
    title: "Ce que disent nos clients",
    items: [
      {
        name: "Aqua Tiles",
        text: "SocialFlow nous a aidés à automatiser les demandes et à augmenter les visites en showroom.",
      },
      {
        name: "Professeur d'anglais de Mysore",
        text: "Je ne savais pas que l'image de marque comptait avant que SocialFlow structure ma présence.",
      },
      {
        name: "Pallav",
        text: "Plus de demandes de vente de terrains en 30 jours qu'au cours des 6 derniers mois.",
      },
    ],
  },
  finalCta: {
    badge: "COMMENCER",
    title: "Prêt à mettre votre entreprise en pilote automatique ?",
    subtitle:
      "Dites-nous où vous êtes bloqué et nous cartographierons l'automatisation qui vous fait gagner le plus de temps — gratuitement.",
    button: "Réserver un audit gratuit",
  },
  serviceSlides: {
    "ai-development": {
      title: "Développement IA",
      tagline: "Mettez l'IA au travail dans votre entreprise.",
      tag: "IA et apprentissage automatique",
    },
    "ai-agents": {
      title: "Agents IA",
      tagline: "Des collègues numériques qui terminent la tâche.",
      tag: "IA et apprentissage automatique",
    },
    "api-development": {
      title: "Développement d'API",
      tagline: "Connectez les systèmes pour que les données circulent seules.",
      tag: "Logiciels et intégrations",
    },
    "crm-integration": {
      title: "Déploiement et intégration CRM",
      tagline: "Chaque prospect et client au même endroit.",
      tag: "Logiciels et intégrations",
    },
    "system-integrations": {
      title: "Intégrations système",
      tagline: "Faites travailler tous vos outils comme un seul.",
      tag: "Logiciels et intégrations",
    },
    "cloud-infrastructure": {
      title: "Cloud, serveurs et infrastructure",
      tagline: "Hébergement fiable, et des experts à la demande.",
      tag: "Cloud et infrastructure",
    },
    "messaging-services": {
      title: "Services SMS et e-mail",
      tagline: "Touchez vos clients là où ils regardent vraiment.",
      tag: "Communication",
    },
    "website-development": {
      title: "Développement de sites web",
      tagline: "Des sites rapides et modernes qui convertissent.",
      tag: "Applications et automatisation",
    },
    "mobile-app-development": {
      title: "Développement d'applications mobiles",
      tagline: "Des applications que vos clients et votre équipe adorent utiliser.",
      tag: "Applications et automatisation",
    },
    "mobile-workflow-automation": {
      title: "Automatisation des flux mobiles",
      tagline: "Vos processus, pilotés depuis un téléphone.",
      tag: "Applications et automatisation",
    },
  },
};

export const HOME_COPY: Record<LanguageCode, HomeCopy> = {
  en: { ...EN },
  hi,
  fr,
};
